import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import type { BoardObject } from '../interfaces/board-object.interface.js';

/**
 * Remove an object by id. On `apply` we snapshot the full object so `revert`
 * can put it back exactly as it was — otherwise undoing a delete would lose
 * the object's properties.
 *
 * @deprecated Use {@link RemoveObjectWithAnchorsCommand} instead. This command
 * does not cascade-clear line anchors that reference the removed object,
 * which leaves dangling anchor references after undo (H1-Bug, PRD-D). The
 * successor `RemoveObjectWithAnchorsCommand([id])` snapshots and patches all
 * affected line anchors as part of the same composite mutation, so revert
 * restores both the removed object and the original anchor wiring atomically.
 * Retained as a public re-export for backward compatibility with existing
 * callers; will be removed in a follow-up PRD once all consumers have migrated.
 */
export class RemoveObjectCommand implements Command<BoardState> {
  readonly label = 'Objekt löschen';
  private snapshot?: BoardObject;

  constructor(private readonly _id: string) {}

  /** Public read-only accessor for the id of the object being removed. */
  get id(): string {
    return this._id;
  }

  apply(state: BoardState): void {
    this.snapshot = state.getObject(this._id);
    state.removeObject(this._id);
  }

  revert(state: BoardState): void {
    if (this.snapshot) state.addObject(this.snapshot);
  }
}
